import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { BrowserMultiFormatReader } from "@zxing/browser";
import * as regularAdminServices from "../services/api";
import * as superAdminServices from "../services/superAdminApi";
import { BarcodeGraphic, BarcodePrintLabel, downloadBarcodeLabelPng, printBarcodeLabel } from "../components/BarcodeLabel";
import { PageHeader } from "../components/UI";

const STATUS_FILTERS = ["all", "available", "sold", "inactive", "archived"];
const BARCODE_PAGE_SIZE = 12;
const SUPERADMIN_SERVICES = { ...superAdminServices };
const effectiveStatus = (size) => size.archived ? "archived" : (size.status || (size.isAvailable === false ? "inactive" : "available"));

function inventoryRows(products) {
  return (products || []).flatMap((product) => (product.variants || []).flatMap((variant) => (variant.sizes || []).map((size) => ({
    product,
    variant,
    size,
    sku: size.sku || size.barcode || "",
    status: effectiveStatus(size),
  })))).filter((row) => row.sku);
}

function downloadReport(records, filename) {
  const headings = ["SKU", "Product", "Result"];
  const csv = [headings, ...(records || []).map((record) => [record.sku, record.productName || "", record.result])]
    .map((line) => line.map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\r\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export default function BarcodeManagement({ superAdmin = false }) {
  const services = superAdmin ? SUPERADMIN_SERVICES : regularAdminServices;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [listSearch, setListSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [details, setDetails] = useState(null);
  const [searching, setSearching] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [report, setReport] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const cameraRef = useRef(null);
  const inputRef = useRef(null);
  const labelRef = useRef(null);

  const loadInventory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await services.getBarcodeInventory();
      setRows(inventoryRows(response.data?.products));
    } catch {
      toast.error("Unable to load barcode inventory.");
    } finally {
      setLoading(false);
    }
  }, [services]);

  useEffect(() => { loadInventory(); }, [loadInventory]);

  const lookup = useCallback(async (rawSku) => {
    const sku = rawSku.trim();
    if (!sku) return;
    setSearching(true);
    setDetails(null);
    try {
      const response = await services.findProductByBarcode(sku);
      setDetails(response.data);
      setSearch(response.data.sku || sku);
    } catch (error) {
      setDetails(null);
      if (error.response?.status === 404) toast.error("Product not found for this barcode.");
      else toast.error(error.response?.data?.message || "Barcode lookup failed.");
    } finally {
      setSearching(false);
    }
  }, [services]);

  useEffect(() => {
    if (!cameraOpen) return undefined;
    let stream;
    let frameId;
    let reader;
    let controls;
    let cancelled = false;
    let isDetecting = false;
    const detectorConstructor = window.BarcodeDetector;
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.info("Camera access requires HTTPS or localhost. You can also connect a USB scanner or enter the SKU.");
      setCameraOpen(false);
      return undefined;
    }
    const start = async () => {
      try {
        if (!detectorConstructor) {
          reader = new BrowserMultiFormatReader();
          controls = await reader.decodeFromVideoDevice(undefined, cameraRef.current, (result) => {
            if (cancelled || !result) return;
            const value = result.getText().trim();
            if (!value) return;
            cancelled = true;
            controls?.stop();
            setSearch(value);
            setCameraOpen(false);
            lookup(value);
          });
          return;
        }

        const detector = new detectorConstructor({ formats: ["code_128"] });
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
        if (cancelled) { stream.getTracks().forEach((track) => track.stop()); return; }
        if (cameraRef.current) {
          cameraRef.current.srcObject = stream;
          await cameraRef.current.play();
        }
        const scan = async () => {
          if (cancelled) return;
          if (!isDetecting && cameraRef.current?.readyState >= 2) {
            isDetecting = true;
            try {
              const detections = await detector.detect(cameraRef.current);
              if (detections[0]?.rawValue) {
                const value = detections[0].rawValue;
                setSearch(value);
                setCameraOpen(false);
                lookup(value);
                return;
              }
            } catch { /* Keep the preview running while the camera adjusts. */ }
            isDetecting = false;
          }
          frameId = window.requestAnimationFrame(scan);
        };
        scan();
      } catch {
        toast.error("Camera permission was denied or the camera is unavailable.");
        setCameraOpen(false);
      }
    };
    start();
    return () => {
      cancelled = true;
      if (frameId) window.cancelAnimationFrame(frameId);
      controls?.stop();
      reader?.reset?.();
      stream?.getTracks().forEach((track) => track.stop());
      if (cameraRef.current) cameraRef.current.srcObject = null;
    };
  }, [cameraOpen, lookup]);

  const visibleRows = useMemo(() => rows.filter((row) => {
    const matchesStatus = filter === "all" || row.status === filter;
    const value = listSearch.trim().toLowerCase();
    const matchesSearch = !value || [row.sku, row.size.barcode, row.product.name, row.product.brand].some((field) => String(field || "").toLowerCase().includes(value));
    return matchesStatus && matchesSearch;
  }), [rows, filter, listSearch]);
  const totalPages = Math.max(1, Math.ceil(visibleRows.length / BARCODE_PAGE_SIZE));
  const page = Math.min(currentPage, totalPages);
  const paginatedRows = visibleRows.slice((page - 1) * BARCODE_PAGE_SIZE, page * BARCODE_PAGE_SIZE);

  const downloadLabel = async () => {
    if (!details) return;
    try {
      const svg = labelRef.current?.querySelector("svg");
      await downloadBarcodeLabelPng({ svg, product: details.product, variant: details.variant, size: details.size, sku: details.sku });
      const { data: tracking } = await services.recordBarcodeDownload(details.product._id, details.sku);
      setDetails((current) => ({ ...current, size: { ...current.size, barcodeDownloaded: true, barcodeDownloadedAt: tracking.barcodeDownloadedAt, barcodeDownloadCount: tracking.barcodeDownloadCount } }));
      toast.success("Barcode label downloaded successfully.");
      loadInventory();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Could not download barcode label.");
    }
  };

  const downloadBarcodeImage = async () => {
    if (!details) return;
    try {
      const response = await services.getBarcodePng(details.sku);
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${details.sku}-barcode.png`;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      const { data: tracking } = await services.recordBarcodeDownload(details.product._id, details.sku);
      setDetails((current) => ({ ...current, size: { ...current.size, barcodeDownloaded: true, barcodeDownloadedAt: tracking.barcodeDownloadedAt, barcodeDownloadCount: tracking.barcodeDownloadCount } }));
      toast.success("Code 128 barcode PNG downloaded.");
      loadInventory();
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not generate barcode image.");
    }
  };

  const markAsSold = async () => {
    if (!details || details.status !== "available") return;
    if (isProductBarcodeLookup) {
      toast.info("Search by the variant SKU to update its inventory status.");
      return;
    }
    try {
      await services.updateSkuStatus(details.product._id, { sku: details.sku, status: "sold", saleType: "offline" });
      setDetails((current) => ({ ...current, status: "sold", size: { ...current.size, status: "sold", saleType: "offline", soldAt: new Date().toISOString(), isAvailable: false } }));
      toast.success("Product marked as sold.");
      loadInventory();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to update product status.");
    }
  };

  const archiveSku = async () => {
    if (!details) return;
    if (isProductBarcodeLookup) {
      toast.info("Search by the variant SKU to archive its inventory.");
      return;
    }
    try {
      await services.updateSkuStatus(details.product._id, { sku: details.sku, status: "inactive", archived: true });
      setDetails((current) => ({ ...current, status: "archived", size: { ...current.size, status: "inactive", archived: true, isAvailable: false } }));
      toast.success("Product archived.");
      loadInventory();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to archive product.");
    }
  };

  const permanentlyDelete = async () => {
    if (isProductBarcodeLookup) {
      toast.info("Search by the variant SKU before deleting inventory.");
      return;
    }
    if (!details || !window.confirm(`Permanently delete ${details.product.name} (${details.sku})? This cannot be undone.`)) return;
    try {
      await services.deleteProduct(details.product._id, true);
      toast.success("Product permanently deleted.");
      setDetails(null);
      loadInventory();
    } catch (error) {
      toast.error(error.response?.data?.message || "Permanent deletion failed.");
    }
  };

  const handleFilePreview = async (event) => {
    event.preventDefault();
    if (!file) { toast.error("Choose a CSV or Excel file first."); return; }
    setUploading(true);
    setReport(null);
    try {
      const response = await services.previewOfflineSale(file);
      setPreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not validate the uploaded file.");
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmSale = async () => {
    if (!preview?.confirmSkus?.length) return;
    setConfirming(true);
    try {
      const response = await services.confirmOfflineSale(preview.records.map((record) => record.sku));
      setReport(response.data);
      setPreview(null);
      setFile(null);
      toast.success(`${response.data.processed} products marked as sold.`);
      if (response.data.alreadySold) toast.warning(`${response.data.alreadySold} products were already sold.`);
      if (response.data.notFound) toast.error(`${response.data.notFound} uploaded SKUs were not found.`);
      if (response.data.duplicates) toast.warning(`${response.data.duplicates} duplicate rows were skipped.`);
      if (response.data.failed) toast.error(`${response.data.failed} products could not be processed.`);
      loadInventory();
    } catch (error) {
      toast.error(error.response?.data?.message || "Offline sale could not be completed.");
    } finally {
      setConfirming(false);
    }
  };

  const detailsProduct = details?.product;
  const detailsStatus = details?.status || (details?.size && effectiveStatus(details.size));
  const isProductBarcodeLookup = Boolean(
    details && details.barcode === String(details.product._id) && details.sku !== details.barcode
  );

  return (
    <div className={`space-y-6 ${details ? "barcode-product-focused" : ""}`}>
      <PageHeader title="SKU & Barcode Inventory" />
      <section className="kanchira-card space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-[#640101]">Search SKU / Scan Barcode</h2>
          <p className="mt-1 text-sm text-gray-500">USB scanners work as keyboard input. Scan the label or enter the SKU to fetch the product from the server.</p>
        </div>
        <form className="flex flex-col gap-2 sm:flex-row" onSubmit={(event) => { event.preventDefault(); lookup(search); }}>
          <input ref={inputRef} className="kanchira-input min-w-0 flex-1 font-mono" autoComplete="off" placeholder="e.g. JW-2026-000125" value={search} onChange={(event) => setSearch(event.target.value)} aria-label="Search SKU or barcode" />
          <button className="btn-primary" type="submit" disabled={searching}>{searching ? "Searching..." : "Search"}</button>
          <button className="btn-secondary" type="button" onClick={() => setCameraOpen((open) => !open)}>{cameraOpen ? "Stop Camera" : "Scan with Camera"}</button>
        </form>
        {cameraOpen && <div className="max-w-lg overflow-hidden rounded-xl bg-black"><video ref={cameraRef} className="aspect-video w-full object-cover" muted playsInline /><p className="p-2 text-center text-xs text-white">Point your camera at a Code 128 label</p></div>}

        {details && detailsProduct && (
          <div className="grid gap-5 border-t pt-5 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="min-w-0 space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0"><p className="text-xl font-bold text-gray-900">Product Details</p><p className="mt-1 break-all text-lg font-semibold text-gray-800">{detailsProduct.name}</p><p className="font-mono text-sm text-gray-600">{details.sku}</p></div>
                <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase ${detailsStatus === "available" ? "bg-green-100 text-green-700" : detailsStatus === "sold" ? "bg-rose-100 text-rose-700" : "bg-gray-100 text-gray-700"}`}>{detailsStatus}</span>
              </div>
              {isProductBarcodeLookup && <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">This product barcode identifies the product, not an individual size. Search by variant SKU to change inventory status.</p>}
              <button className="btn-secondary w-full md:hidden" type="button" onClick={() => { setDetails(null); setSearch(""); }}>← Back to Scan</button>
              {detailsProduct.image && <img src={detailsProduct.image} alt={detailsProduct.name} className="h-56 w-full rounded-xl bg-gray-50 object-contain md:hidden" />}
              <div className="rounded-xl border border-gray-200 bg-white p-3 md:hidden">
                <BarcodeGraphic value={details.barcode || details.sku} height={64} className="mx-auto h-auto w-full max-w-sm" />
                <p className="mt-1 text-center font-mono text-sm tracking-wider">{detailsProduct._id || details.sku}</p>
                {details.size?.barcodeDownloadedAt && <p className="mt-3 rounded-lg bg-green-50 p-3 text-center text-xs font-semibold text-green-700">✓ Downloaded {new Date(details.size.barcodeDownloadedAt).toLocaleString()}</p>}
                <button className="btn-primary mt-3 w-full" type="button" onClick={downloadBarcodeImage}>Download Barcode (PNG)</button>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[
                  ["Brand", detailsProduct.brand], ["Category", detailsProduct.categoryId?.name],
                  ["Material", detailsProduct.material], ["Weight", detailsProduct.weight != null ? `${detailsProduct.weight} g` : "—"],
                  ["Gender", detailsProduct.gender], ["Color", details.variant?.color], ["Size", details.size?.size],
                  ["Sub-category", detailsProduct.subcategoryId?.name], ["Sub-subcategory", detailsProduct.subsubcategoryId?.name],
                  ["Slug", detailsProduct.slug], ["Speciality", detailsProduct.speciality],
                  ["Tags", Array.isArray(detailsProduct.tags) ? detailsProduct.tags.join(", ") : detailsProduct.tags],
                  ["Product discount", detailsProduct.discountPercentage != null ? `${detailsProduct.discountPercentage}%` : "—"],
                  ["SKU", details.sku], ["Product ID", detailsProduct.productId || "—"], ["Barcode value", details.barcode || details.sku],
                  ["Price", `₹${Number(details.price ?? details.size?.price ?? 0).toLocaleString("en-IN")}`],
                  ["Offer price", `₹${Number(details.size?.finalPrice ?? details.size?.price ?? 0).toLocaleString("en-IN")}`],
                  ["Size discount", `${details.size?.discountPercentage ?? 0}%`],
                  ["Stock", details.size?.stock ?? 0], ["Available units", details.size?.left ?? 0],
                  ["Availability", details.size?.isAvailable === false ? "No" : "Yes"],
                  ["Product status", detailsProduct.available === false ? "Inactive" : "Active"],
                  ["Sales status", details.size?.saleType || "—"], ["Sold date", details.size?.soldAt ? new Date(details.size.soldAt).toLocaleString() : "—"],
                  ["Created", detailsProduct.createdAt ? new Date(detailsProduct.createdAt).toLocaleDateString() : "—"],
                  ["Updated", detailsProduct.updatedAt ? new Date(detailsProduct.updatedAt).toLocaleDateString() : "—"],
                  ["Barcode downloads", details.size?.barcodeDownloadCount ?? 0],
                ].map(([label, value]) => <div key={label} className="min-w-0 rounded-lg bg-gray-50 p-3"><p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</p><p className="mt-1 break-words text-sm font-medium text-gray-800">{value || "—"}</p></div>)}
              </div>
              {detailsProduct.description && <div className="rounded-lg bg-gray-50 p-3"><p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Description</p><p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{detailsProduct.description}</p></div>}
              {(() => {
                const images = [detailsProduct.image, ...(detailsProduct.galleryImages || []).map((image) => typeof image === "string" ? image : image?.url), ...(details.variant?.images || []).map((image) => image?.url)].filter(Boolean);
                return images.length > 0 && <div><p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Product Images</p><div className="flex flex-wrap gap-2">{images.map((image, index) => <img key={`${image}-${index}`} src={image} alt={`${detailsProduct.name} ${index + 1}`} className="h-20 w-20 rounded-lg border bg-white object-cover" />)}</div></div>;
              })()}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-800">All Variants & Sizes</h3>
                {(detailsProduct.variants || []).map((variant, variantIndex) => (
                  <div key={variant._id || variantIndex} className="overflow-hidden rounded-xl border border-gray-200">
                    <div className="flex items-center gap-2 bg-gray-50 px-3 py-2">
                      {variant.colorCode && <span className="h-4 w-4 rounded-full border" style={{ backgroundColor: variant.colorCode }} />}
                      <span className="text-sm font-semibold text-gray-800">{variant.color || "Variant"}</span>
                      {variant.colorCode && <span className="text-xs text-gray-500">{variant.colorCode}</span>}
                    </div>
                    {variant.images?.length > 0 && <div className="flex flex-wrap gap-2 p-3">{variant.images.map((image, imageIndex) => <div key={`${image.url}-${imageIndex}`} className="text-center"><img src={image.url} alt={image.alt || `${variant.color} ${imageIndex + 1}`} className="h-16 w-16 rounded-md border object-cover" />{image.alt && <p className="mt-1 max-w-16 truncate text-[10px] text-gray-500">{image.alt}</p>}</div>)}</div>}
                    <div className="hidden overflow-x-auto sm:block">
                      <table className="w-full text-left text-xs">
                        <thead className="bg-white text-gray-500"><tr>{["Size", "Price", "Stock", "Left", "SKU / Barcode", "Discount", "Final price", "Available", "Status"].map((heading) => <th key={heading} className="whitespace-nowrap border-t px-2 py-2 font-medium">{heading}</th>)}</tr></thead>
                        <tbody>{(variant.sizes || []).map((size, sizeIndex) => {
                          const isScannedSku = size.sku === details.sku;
                          return <tr key={size._id || sizeIndex} className={isScannedSku ? "bg-amber-50" : "even:bg-gray-50"}>
                            <td className="whitespace-nowrap px-2 py-2 font-medium">{size.size || "—"}</td>
                            <td className="whitespace-nowrap px-2 py-2">₹{Number(size.price || 0).toLocaleString("en-IN")}</td>
                            <td className="px-2 py-2">{size.stock ?? 0}</td><td className="px-2 py-2">{size.left ?? 0}</td>
                            <td className="min-w-[145px] px-2 py-2">{size.sku ? <><BarcodeGraphic value={size.barcode || size.sku} height={30} className="h-auto w-32" /><span className="block text-center font-mono">{detailsProduct._id || size.sku}</span></> : "—"}</td>
                            <td className="px-2 py-2">{size.discountPercentage ?? 0}%</td>
                            <td className="whitespace-nowrap px-2 py-2">₹{Number(size.finalPrice ?? size.price ?? 0).toLocaleString("en-IN")}</td>
                            <td className="px-2 py-2">{size.isAvailable === false ? "No" : "Yes"}</td>
                            <td className="px-2 py-2">{effectiveStatus(size)}</td>
                          </tr>;
                        })}</tbody>
                      </table>
                    </div>
                    <div className="space-y-2 p-3 sm:hidden">
                      {(variant.sizes || []).map((size, sizeIndex) => (
                        <div key={size._id || sizeIndex} className={`rounded-lg border p-3 ${size.sku === details.sku ? "border-amber-400 bg-amber-50" : "border-gray-200 bg-white"}`}>
                          <div className="flex items-center justify-between gap-2"><strong className="text-sm">Size {size.size || "—"}</strong><span className="text-xs capitalize text-gray-500">{effectiveStatus(size)}</span></div>
                          <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-600">
                            <span>Price: ₹{Number(size.price || 0).toLocaleString("en-IN")}</span><span>Offer: ₹{Number(size.finalPrice ?? size.price ?? 0).toLocaleString("en-IN")}</span>
                            <span>Stock: {size.stock ?? 0}</span><span>Left: {size.left ?? 0}</span><span>Discount: {size.discountPercentage ?? 0}%</span><span>Available: {size.isAvailable === false ? "No" : "Yes"}</span>
                          </div>
                          {size.sku && <div className="mt-3 max-w-xs rounded bg-white p-2"><BarcodeGraphic value={size.barcode || size.sku} height={42} className="h-auto w-full" /><p className="text-center font-mono text-xs">{detailsProduct._id || size.sku}</p></div>}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {detailsStatus === "available" && <button className="btn-primary" type="button" onClick={markAsSold} disabled={isProductBarcodeLookup}>Mark as Sold</button>}
                {!details.size?.archived && <button className="btn-secondary" type="button" onClick={archiveSku} disabled={isProductBarcodeLookup}>Archive</button>}
                <button className="btn-secondary" type="button" onClick={() => { try { printBarcodeLabel({ svg: labelRef.current?.querySelector("svg"), product: details.product, variant: details.variant, size: details.size, sku: details.sku }); } catch (error) { toast.error(error.message); } }}>Print Label</button>
                <button className="btn-secondary" type="button" onClick={permanentlyDelete} disabled={isProductBarcodeLookup}>Delete Permanently</button>
              </div>
              {details.size?.barcodeDownloadedAt && <p className="text-xs text-green-700">✓ Barcode downloaded {new Date(details.size.barcodeDownloadedAt).toLocaleString()}</p>}
            </div>
            <div ref={labelRef} className="hidden md:block">
              {detailsProduct.image && <img src={detailsProduct.image} alt={detailsProduct.name} className="mb-3 h-36 w-full rounded-xl bg-gray-50 object-contain" />}
              <BarcodePrintLabel product={detailsProduct} variant={details.variant} size={details.size} sku={details.sku} />
              {details.size?.barcodeDownloadedAt && <p className="mt-3 rounded-lg bg-green-50 p-3 text-center text-xs font-semibold text-green-700">✓ Downloaded {new Date(details.size.barcodeDownloadedAt).toLocaleString()}</p>}
              <button className="btn-primary mt-3 w-full" type="button" onClick={downloadBarcodeImage}>Download Barcode (PNG)</button>
              <button className="btn-secondary mt-2 w-full" type="button" onClick={downloadLabel}>Download Full Label (PNG)</button>
              <p className="mt-2 text-center text-xs text-gray-500">Print opens the browser print dialog; choose Save as PDF for a PDF label.</p>
            </div>
          </div>
        )}
      </section>

      <section className="kanchira-card space-y-4 barcode-management-secondary">
        <div>
          <h2 className="text-lg font-semibold text-[#640101]">Offline Sale / Bulk Stock Update</h2>
          <p className="mt-1 text-sm text-gray-500">Upload CSV/XLSX with a SKU or Barcode column. Use variant SKUs for offline sales; a product-ID barcode cannot identify a specific size. Upload only validates; inventory changes after confirmation.</p>
        </div>
        <form onSubmit={handleFilePreview} className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className="min-w-0 flex-1 text-sm font-medium text-gray-700">CSV or Excel file (max 5 MB, 1,000 rows)
            <input className="mt-1 block w-full rounded-lg border border-gray-300 p-2 text-sm" type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(event) => { setFile(event.target.files?.[0] || null); setPreview(null); }} />
          </label>
          <button className="btn-primary" type="submit" disabled={uploading}>{uploading ? "Validating..." : "Validate & Preview"}</button>
        </form>
        {preview && <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50/50 p-4">
          <h3 className="font-semibold text-gray-800">Preview — no products have been changed</h3>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            {[["Total uploaded", preview.totalUploaded], ["Valid products", preview.validProducts], ["Already sold", preview.alreadySold], ["Not found", preview.notFound], ["Duplicates", preview.duplicates]].map(([label, count]) => <div key={label} className="rounded-lg bg-white p-3"><p className="text-xs text-gray-500">{label}</p><p className="text-xl font-bold text-[#640101]">{count}</p></div>)}
          </div>
          <div className="max-h-72 overflow-auto rounded-lg border bg-white">
            <table className="w-full text-left text-sm"><thead className="sticky top-0 bg-gray-100"><tr><th className="p-2">SKU</th><th className="p-2">Product</th><th className="p-2">Current status</th><th className="p-2">Result</th></tr></thead><tbody>{preview.records.map((record, index) => <tr key={`${record.sku}-${index}`} className="border-t"><td className="p-2 font-mono">{record.sku}</td><td className="p-2">{record.productName || "—"}</td><td className="p-2">{record.currentStatus}</td><td className={`p-2 font-medium ${record.result === "Will be Sold" ? "text-green-700" : "text-amber-700"}`}>{record.result}</td></tr>)}</tbody></table>
          </div>
          <div className="flex flex-wrap justify-end gap-2"><button className="btn-secondary" type="button" onClick={() => { setPreview(null); setFile(null); }}>Cancel</button><button className="btn-primary" type="button" onClick={handleConfirmSale} disabled={confirming || !preview.confirmSkus?.length}>{confirming ? "Processing..." : `Confirm Offline Sale (${preview.confirmSkus?.length || 0})`}</button></div>
        </div>}
        {report && <div className="rounded-xl border border-green-200 bg-green-50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2"><h3 className="font-semibold text-green-900">Bulk Offline Sale Completed</h3><button type="button" className="btn-secondary" onClick={() => downloadReport(report.records, "offline-sale-report.csv")}>Download Report</button></div>
          <p className="mt-2 text-sm text-green-900">Processed: {report.processed} · Already sold: {report.alreadySold} · Not found: {report.notFound} · Duplicates: {report.duplicates} · Failed/unavailable: {report.failed}</p>
        </div>}
      </section>

      <section className="kanchira-card space-y-4 barcode-management-secondary">
        <div className="flex flex-wrap items-end justify-between gap-3"><div><h2 className="text-lg font-semibold text-[#640101]">Product Barcode Inventory</h2><p className="text-sm text-gray-500">Search by product name, SKU, or barcode. Status changes are persisted by the backend.</p></div><input className="kanchira-input w-full sm:max-w-xs" value={listSearch} onChange={(event) => { setListSearch(event.target.value); setCurrentPage(1); }} placeholder="Search name, SKU, or barcode..." /></div>
        <div className="flex flex-wrap gap-2">{STATUS_FILTERS.map((status) => <button key={status} type="button" onClick={() => { setFilter(status); setCurrentPage(1); }} className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize ${filter === status ? "bg-[#640101] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{status}</button>)}</div>
        {loading ? <div className="py-8 text-center text-gray-500">Loading inventory…</div> : visibleRows.length === 0 ? <div className="py-8 text-center text-gray-500">No matching barcode inventory found.</div> : <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">          {paginatedRows.map((row) => <button type="button" key={`${row.product._id}-${row.sku}`} onClick={() => { setSearch(row.sku); lookup(row.sku); }} className="min-w-0 rounded-xl border border-gray-200 bg-white p-4 text-left transition hover:border-[#D2AE4E] hover:shadow-sm"><span className="flex items-start justify-between gap-2"><span className="min-w-0"><span className="block truncate font-semibold text-gray-900">{row.product.name}</span></span><span className="rounded-full bg-gray-100 px-2 py-1 text-[10px] font-bold uppercase text-gray-600">{row.status}</span></span><span className="mt-3 block rounded bg-white p-2"><BarcodeGraphic value={row.size.barcode || row.sku} height={48} className="h-auto w-full" /><span className="mt-1 block text-center font-mono text-xs tracking-wider text-gray-700">{row.product._id || row.sku}</span></span><span className="mt-2 block text-sm text-gray-600">{row.variant.color}{row.size.size ? ` · ${row.size.size}` : ""} · ₹{Number(row.size.finalPrice ?? row.size.price ?? 0).toLocaleString("en-IN")}</span><span className="mt-1 block text-xs text-gray-400">Downloads: {row.size.barcodeDownloadCount || 0}</span></button>)}</div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
            <p className="text-sm text-gray-600">Showing {(page - 1) * BARCODE_PAGE_SIZE + 1}–{Math.min(page * BARCODE_PAGE_SIZE, visibleRows.length)} of {visibleRows.length} barcodes (12 per page)</p>
            {totalPages > 1 && <div className="flex items-center gap-2">
              <button type="button" className="btn-secondary" onClick={() => setCurrentPage(page - 1)} disabled={page === 1}>Previous</button>
              <span className="min-w-20 text-center text-sm text-gray-600">Page {page} of {totalPages}</span>
              <button type="button" className="btn-secondary" onClick={() => setCurrentPage(page + 1)} disabled={page === totalPages}>Next</button>
            </div>}
          </div>
        </>}
      </section>
      <style>{`@media (max-width: 767px) { .barcode-product-focused .barcode-management-secondary { display: none; } } @media print { body * { visibility: hidden !important; } .barcode-print-area, .barcode-print-area * { visibility: visible !important; } .barcode-print-area { position: fixed !important; left: 0 !important; top: 0 !important; width: 70mm !important; max-width: 70mm !important; border: 0 !important; box-shadow: none !important; padding: 5mm !important; } }`}</style>
    </div>
  );
}
